import React, { useEffect, useState } from 'react'
import { FaPlus, FaSpinner, FaTags } from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, Modal, Toast, useToast, EmptyState } from '../components/ui'
import { number } from '../lib/format'
import * as api from '../lib/api'
import { useAuth } from '../context/AuthContext'

/**
 * Categories — the filter pills along the top of the shop.
 *
 * `sort_order` is exposed as a plain number rather than hidden behind drag and
 * drop. The order is a merchandising decision the owner makes deliberately
 * (Polo first, accessories last), not something worth a drag library, and a
 * number is unambiguous about what it will do.
 */
const CategoriesPage = () => {
  // Category changes need is_manager; staff get the list without the controls.
  const { canManage } = useAuth()
  const [categories, setCategories] = useState([])
  const [state, setState] = useState({ loading: true, error: null })
  const [editing, setEditing] = useState(null)
  const [busy, setBusy] = useState(false)
  const { toast, notify, notifyError, dismiss } = useToast()

  const load = async () => {
    setState({ loading: true, error: null })
    try {
      setCategories(await api.getCategories())
      setState({ loading: false, error: null })
    } catch (err) {
      setState({ loading: false, error: err })
    }
  }

  useEffect(() => {
    load()
  }, [])

  const save = async () => {
    setBusy(true)
    try {
      if (!editing.name?.trim()) throw new Error('A category needs a name.')
      const payload = {
        name: editing.name.trim(),
        // Generated from the name when left blank, matching the SQL slugify().
        slug:
          editing.slug?.trim() ||
          editing.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        icon: editing.icon?.trim() || null,
        sort_order: Number(editing.sort_order) || 0,
        is_active: editing.is_active !== false,
      }
      await api.saveCategory(editing.id ?? null, payload)
      notify(editing.id ? 'Category saved.' : 'Category created.')
      setEditing(null)
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  if (state.loading) return <Spinner label="Loading categories…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load categories.</strong> {state.error.message}</Banner>
  }

  return (
    <div>
      <PageHeader title="Categories" subtitle="The filters shown along the top of the shop">
        {canManage && (
          <button
            onClick={() => setEditing({ name: '', slug: '', icon: 'FaTshirt', sort_order: (categories.length + 1) * 10, is_active: true })}
            className="btn-primary"
          >
            <FaPlus className="text-[11px]" />
            Add a category
          </button>
        )}
      </PageHeader>

      <Card bodyClassName="">
        {categories.length === 0 ? (
          <EmptyState icon={FaTags} title="No categories yet">
            Categories decide how shoppers filter the catalogue.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="num">Order</th>
                  <th>Name</th>
                  <th>URL slug</th>
                  <th className="num">Products</th>
                  <th>Shown</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td className="num text-ink-muted">{category.sort_order}</td>
                    <td className="font-medium">{category.name}</td>
                    <td className="font-mono text-2xs text-ink-secondary">{category.slug}</td>
                    <td className="num">{number(category.product_count)}</td>
                    <td>
                      {category.is_active ? (
                        <span className="text-2xs font-medium text-status-good-text">Visible</span>
                      ) : (
                        <span className="text-2xs text-ink-muted">Hidden</span>
                      )}
                    </td>
                    <td className="text-right">
                      {canManage && (
                        <button onClick={() => setEditing(category)} className="btn-subtle btn-sm">
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit category' : 'New category'}
        footer={
          <>
            <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary">
              {busy && <FaSpinner className="animate-spin" />}
              Save
            </button>
          </>
        }
      >
        {editing && (
          <div className="space-y-3.5">
            <div>
              <label className="label" htmlFor="cat-name">Name</label>
              <input
                id="cat-name"
                className="input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="e.g. Golf Shirts"
                autoFocus
              />
              <p className="field-hint">Shown on the filter pill exactly as typed.</p>
            </div>

            <div>
              <label className="label" htmlFor="cat-slug">URL slug</label>
              <input
                id="cat-slug"
                className="input font-mono"
                value={editing.slug ?? ''}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                placeholder="Left blank, generated from the name"
              />
            </div>

            <div className="grid gap-3.5 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="cat-order">Display order</label>
                <input
                  id="cat-order"
                  type="number"
                  className="input"
                  value={editing.sort_order ?? 0}
                  onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                />
                <p className="field-hint">Lower numbers appear first.</p>
              </div>

              <div>
                <label className="label" htmlFor="cat-icon">Icon</label>
                <select
                  id="cat-icon"
                  className="input"
                  value={editing.icon ?? ''}
                  onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                >
                  <option value="FaTshirt">Shirt</option>
                  <option value="FaUserTie">Trousers</option>
                  <option value="FaJedi">Outerwear</option>
                  <option value="FaSocks">Socks</option>
                  <option value="FaShoePrints">Shoes</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2.5 text-[13px]">
              <input
                type="checkbox"
                checked={editing.is_active !== false}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                className="h-4 w-4 rounded border-hairline text-brand-navy focus:ring-brand-navy/30"
              />
              Show this category on the shop
            </label>
          </div>
        )}
      </Modal>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

export default CategoriesPage
